/* The three things this app can bulk-import, as three descriptors.
 *
 * They were three near-identical class components before this (`import/ImportCore.js`,
 * `import-advanced/ImportAdvanced.js`, `import-amatrix/ImportAMatrix.js`, ~1,140 lines) whose
 * `uploadFile` / `getUploadData` / `validate` / `submit` / `changeFilter` / `handleChangeBulkName`
 * and whole `state` shape were copy-pasted. They differ in exactly four ways — the endpoint, the
 * column contract, whether a template or a scope is needed, and the payload shape — so that is
 * what a descriptor carries and everything else is the one page in `Import.js`.
 *
 * This is deliberately a plain array of three, not a registry: there are three, they are named in
 * the UI, and a fourth is a backend endpoint away, not a configuration away.
 */

import dayjs from "../../util/dayjs";

/** What the file is expected to hold, and what a template download writes. */
export const DATE_FORMAT = "DD/MM/YYYY";

/* What the API's task deserialiser accepts. Kept exactly as the page this replaces sent it. */
const API_DATETIME = "YYYY-MM-DDTHH:mm";

/* Accepted on the way IN, strictly. A spreadsheet cell reaches us as whatever its own number
   format rendered, and a hand-typed CSV is whatever the author's locale writes, so several real
   shapes arrive — but each is parsed strictly, so `31/13/2026` is rejected rather than rolled
   forward into 2027. */
const DATE_FORMATS = ["DD/MM/YYYY", "D/M/YYYY", "DD.MM.YYYY", "D.M.YYYY", "YYYY-MM-DD"];

/** A cell's date, or null. */
export const parseDate = text => {
  const parsed = dayjs(String(text ?? "").trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * A date field holds **either** a dayjs (typed in the editor, or parsed from a good cell) **or**
 * the raw string that failed to parse — which is what lets the preview show the user the text that
 * is actually in their file instead of an empty cell.
 */
export const asDate = value => (dayjs.isDayjs(value) ? (value.isValid() ? value : null) : parseDate(value));

const trim = value => String(value ?? "").trim();

/* ------------------------------------------------------------------ tasks */

/* Shared by both task kinds — the only difference is where the point and network come from. */
const TASK_COLUMNS = [
  { key: "title", label: "col_title", type: "text", required: true, width: 240 },
  { key: "description", label: "col_description", type: "text", required: false, width: 260 },
  { key: "start", label: "col_start", type: "date", required: true, width: 150 },
  { key: "end", label: "col_end", type: "date", required: true, width: 150 },
];

const ROW_SCOPE_COLUMNS = [
  { key: "point", label: "col_point", type: "code", required: false, width: 140 },
  { key: "network", label: "col_network", type: "code", required: false, width: 140 },
];

const validateTask = row => {
  const errors = {};
  if (!trim(row.title)) errors.title = "e_required";

  ["start", "end"].forEach(key => {
    if (!trim(row[key]) && !dayjs.isDayjs(row[key])) errors[key] = "e_required";
    else if (!asDate(row[key])) errors[key] = "e_date";
  });

  const start = asDate(row.start);
  const end = asDate(row.end);
  /* Only meaningful once both parsed; otherwise the row already carries the truer message. */
  if (start && end && end.isBefore(start, "day")) errors.end = "e_end_before_start";
  return errors;
};

const taskPayload = row => ({
  title: trim(row.title),
  description: trim(row.description),
  start: asDate(row.start).format(API_DATETIME),
  end: asDate(row.end).format(API_DATETIME),
  point: trim(row.point),
  network: trim(row.network),
  images: (row.images || []).map(image => image.id).filter(Boolean),
});

/* --------------------------------------------------------- the descriptors */

export const KINDS = [
  {
    key: "tasks",
    label: "kind_tasks",
    desc: "kind_tasks_desc",
    endpoint: "tasks/bulk_create/",
    accept: ".csv,.xlsx",
    needsTemplate: true,
    /* The API 400s with "No point or network given" when both are empty, so the scope is required
       rather than merely offered — the old page let you submit without one and read the refusal
       back as an opaque red string. */
    needsScope: true,
    editable: true,
    columns: TASK_COLUMNS,

    emptyRow: () => ({ title: "", description: "", start: dayjs(), end: dayjs().add(14, "day"), images: [] }),

    parse: rows =>
      rows.slice(1).map(cells => ({
        title: cells[0] || "",
        description: cells[1] || "",
        start: cells[2] || "",
        end: cells[3] || "",
        images: [],
      })),

    validate: validateTask,

    buildPayload: (form, rows) => {
      /* Every row shares the one scope chosen on the page. */
      const point = form.scopeType === "point" ? form.scope?.code || "" : "";
      const network = form.scopeType === "network" ? form.scope?.code || "" : "";
      return {
        action_name: trim(form.name),
        points: point ? [point] : [],
        networks: network ? [network] : [],
        template: form.template?.id ?? form.template ?? undefined,
        tasks: rows.map(row => taskPayload({ ...row, point, network })),
      };
    },
  },

  {
    key: "tasks_by_row",
    label: "kind_tasks_by_row",
    desc: "kind_tasks_by_row_desc",
    endpoint: "tasks/bulk_create/",
    accept: ".csv,.xlsx",
    needsTemplate: true,
    /* The scope is in the file, one per row — that is the whole point of this kind. */
    needsScope: false,
    /* No hand editor: a batch worth naming a point per row is a batch that came from somewhere. */
    editable: false,
    columns: [...TASK_COLUMNS, ...ROW_SCOPE_COLUMNS],

    parse: rows =>
      rows.slice(1).map(cells => ({
        title: cells[0] || "",
        description: cells[1] || "",
        start: cells[2] || "",
        end: cells[3] || "",
        point: cells[4] || "",
        network: cells[5] || "",
      })),

    validate: row => {
      const errors = validateTask(row);
      /* One or the other, per row. The API takes the union of every row's codes as the batch scope
         and would otherwise be handed an empty one. */
      if (!trim(row.point) && !trim(row.network)) {
        errors.point = "e_point_or_network";
        errors.network = "e_point_or_network";
      }
      return errors;
    },

    buildPayload: (form, rows) => ({
      action_name: trim(form.name),
      points: [...new Set(rows.map(row => trim(row.point)).filter(Boolean))],
      networks: [...new Set(rows.map(row => trim(row.network)).filter(Boolean))],
      template: form.template?.id ?? form.template ?? undefined,
      tasks: rows.map(taskPayload),
    }),
  },

  {
    key: "assortment_matrix",
    label: "kind_matrix",
    desc: "kind_matrix_desc",
    endpoint: "assortment_matrix/bulk_create/",
    accept: ".csv,.xlsx",
    needsTemplate: false,
    needsScope: false,
    editable: false,
    /* Not the file's columns — the file is a grid. These are the PREVIEW's columns; see `parse`. */
    columns: [
      { key: "point", label: "col_point", type: "code", required: true, width: 200 },
      { key: "count", label: "col_products", type: "number", required: false, width: 120 },
    ],

    /**
     * The file is a matrix: row 0 is `<blank>, pointCode, pointCode, …`, every later row is
     * `productCode, mark, mark, …`, and a non-blank cell means "this product belongs in this
     * point's assortment".
     *
     * It is previewed as ONE ROW PER POINT with its product count, not as the raw grid: points ×
     * products is unbounded on both axes, and a 400-column table is not a check anyone can read.
     * The products themselves ride along for the payload and the row's tooltip.
     */
    parse: rows => {
      const header = rows[0] || [];
      const points = header.slice(1).map(trim);
      const byPoint = points.map(point => ({ point, products: [] }));

      rows.slice(1).forEach(cells => {
        const product = trim(cells[0]);
        if (!product) return;
        points.forEach((point, index) => {
          /* `cells[index + 1]` — column 0 is the product code. Any non-blank mark counts; the
             files in use carry `1`, `x` and `+` interchangeably. */
          if (trim(cells[index + 1])) byPoint[index].products.push(product);
        });
      });

      return byPoint.map(row => ({ ...row, count: row.products.length }));
    },

    validate: row => {
      const errors = {};
      if (!trim(row.point)) errors.point = "e_required";
      /* A point column with nothing ticked contributes no key to the payload, so importing it
         would be a no-op the user could not see. */ else if (
        !row.products.length
      )
        errors.count = "e_no_assignments";
      return errors;
    },

    buildPayload: (form, rows) => ({
      action_name: trim(form.name),
      points: rows.map(row => trim(row.point)),
      /* ⚠ A dict keyed by point code, despite the name — the view iterates it with `.items()`.
         Posting a list gets swallowed by its `except Exception` and answers 200 with a failed
         action. */
      assortment_matrix_list: Object.fromEntries(rows.map(row => [trim(row.point), row.products])),
    }),
  },
];

export const getKind = key => KINDS.find(kind => kind.key === key) || KINDS[0];

/**
 * The header row a "download template" writes, plus one example row, so the expected shape is
 * something you can open rather than something you have to infer. The old page shipped no hint at
 * all about what its CSV should contain.
 */
export function templateRows(kind, t) {
  if (kind.key === "assortment_matrix") {
    return [
      ["", "POINT-001", "POINT-002"],
      ["SKU-001", "1", ""],
      ["SKU-002", "1", "1"],
    ];
  }
  const header = kind.columns.map(column => t(column.label));
  const example = kind.columns.map(column => {
    if (column.type === "date") return dayjs().format(DATE_FORMAT);
    if (column.key === "point") return "POINT-001";
    if (column.key === "network") return "";
    if (column.key === "title") return t("sample_title");
    return "";
  });
  return [header, example];
}
